import { supabase } from '../global/supabase';

//////////////////////////////////////////////
// LOCATION CONFIGURATION
//////////////////////////////////////////////
export const LOCATIONS = [
  { id: 'mayo21', name: 'Mayo 21', tableName: 'mayo_21' },
  { id: 'mayo22', name: 'Mayo 22', tableName: 'mayo_22' },
  { id: 'mayo23', name: 'Mayo 23', tableName: 'mayo_23' },
  { id: 'mayo24', name: 'Mayo 24', tableName: 'mayo_24' },
  { id: 'mayo25', name: 'Mayo 25', tableName: 'mayo_25' },
  { id: 'emsstation', name: 'EMS Station', tableName: 'ems_station' }
];

export const SUBSTANCES = [
  'fentanyl',
  'ketamine',
  'etomidate',
  'midazolam10mg_2ml',
  'midazolam5mg_5ml'
];

//////////////////////////////////////////////
// UTILITY FUNCTIONS
//////////////////////////////////////////////

export function formatSubstanceName(substance) {
  return substance
    .replace(/_/g, ' ')
    .replace(/(\d+)/g, ' $1')
    .replace(/ml/g, 'mL')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

export function formatDate(dateString) {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
}

export function getDaysUntilExpiration(expirationDate) {
  if (!expirationDate) return null;

  try {
    const expDate = new Date(expirationDate);
    const today = new Date();

    // Reset time to start of day for accurate day calculation
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch (error) {
    return null;
  }
}

//////////////////////////////////////////////
// EXPIRATION CHECKING FUNCTIONS
//////////////////////////////////////////////

export async function checkLocationExpiration(location) {
  try {
    const { data, error } = await supabase
      .from(location.tableName)
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error(`Error fetching data for ${location.name}:`, error);
      return { location, medications: [], status: 'error' };
    }

    if (!data || data.length === 0) {
      return { location, medications: [], status: 'no-data' };
    }

    const record = data[0];
    const expiringMedications = [];
    let hasRed = false;
    let hasYellow = false;

    SUBSTANCES.forEach(substance => {
      const expirationField = `${substance}_exp`;
      const expirationDate = record[expirationField];

      if (expirationDate) {
        const daysUntil = getDaysUntilExpiration(expirationDate);

        if (daysUntil !== null) {
          let medStatus = 'ok';

          if (daysUntil <= 0) {
            medStatus = 'critical'; // Red - expires today or already expired
            hasRed = true;
          } else if (daysUntil <= 30) {
            medStatus = 'warning'; // Yellow - 30 days or less
            hasYellow = true;
          }

          // Add medications that need attention (30 days or less)
          if (daysUntil <= 30) {
            expiringMedications.push({
              name: formatSubstanceName(substance),
              expirationDate: expirationDate,
              daysUntil: daysUntil,
              status: medStatus
            });
          }
        }
      }
    });

    // Sort medications by days until expiration (most urgent first)
    expiringMedications.sort((a, b) => a.daysUntil - b.daysUntil);

    // Determine overall status: Red > Yellow > OK
    let status = 'ok';
    if (hasRed) {
      status = 'critical';
    } else if (hasYellow) {
      status = 'warning';
    }

    return {
      location,
      medications: expiringMedications,
      status,
      lastChecked: new Date()
    };
  } catch (error) {
    console.error(`Error checking expiration for ${location.name}:`, error);
    return { location, medications: [], status: 'error' };
  }
}

export async function checkAllLocationsExpiration() {
  try {
    const promises = LOCATIONS.map(location => checkLocationExpiration(location));
    const results = await Promise.all(promises);

    // Convert to object keyed by location id for easy lookup
    const expirationData = {};
    results.forEach(result => {
      expirationData[result.location.id] = result;
    });

    return expirationData;
  } catch (error) {
    console.error('Error checking all locations:', error);
    return {};
  }
}
